import {useEffect, useRef, useState} from 'react';
import {initAligningGuidelines} from '~/common/canvas/controls/align_lines';
import fabric from '~/common/canvas/fabric';

const ENVELOPE_IMAGE_DIMENSIONS = {
  width: 531,
  height: 390,
};
const HEIGHT_TO_WIDTH_ENVELOPE_IMAGE_RATIO =
  ENVELOPE_IMAGE_DIMENSIONS.height / ENVELOPE_IMAGE_DIMENSIONS.width;

interface CanvasJSON {
  version?: string;
  objects: fabric.Object[];
}

interface Options {
  configJson?: CanvasJSON;
  onChange: (json: CanvasJSON) => void;
  setCanvas: (c: fabric.Canvas) => void;
  onLoad?: () => void;
}

export const useStickerCanvas = ({configJson, onChange, setCanvas, onLoad}: Options) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!canvasRef.current) {
      return () => {};
    }
    const canvasInstance = new fabric.Canvas(canvasRef.current, {
      width: 0,
      height: 0,
      includeDefaultValues: false,
      selection: !evite.detects.isMobile,
      allowTouchScrolling: true,
    });
    canvasInstance.setZoom(0.5);
    initAligningGuidelines(canvasInstance);

    const setCurrentDimensions = () => {
      if (!canvasInstance) return;

      const canvasSizer = document.getElementById('canvas__wrapper__stickers');

      if (canvasSizer) {
        const width = canvasSizer.offsetWidth;
        const height = canvasSizer.offsetHeight || width * HEIGHT_TO_WIDTH_ENVELOPE_IMAGE_RATIO;
        const scale = width / canvasInstance.getWidth();
        const zoom = canvasInstance.getZoom() * scale;
        canvasInstance.setDimensions({width, height});
        canvasInstance.setViewportTransform([zoom, 0, 0, zoom, 0, 0]);
      }
    };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error Fabric wrapper has no TS types
    canvasInstance.setCurrentDimensions = setCurrentDimensions;

    window.addEventListener('resize', setCurrentDimensions, false);

    const onChangeHandler = () => {
      if (!loading) {
        onChange(canvasInstance.toJSON(['dataUrl', 'delay', 'frameWidth', 'framesLength', 'id']));
      }
    };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error Fabric wrapper has no TS types
    canvasInstance.on({
      'object:modified': onChangeHandler,
      'object:added': onChangeHandler,
      'object:removed': onChangeHandler,
    });

    const loadCanvas = (config: CanvasJSON) => {
      setLoading(true);
      const reviver = async (json: CanvasJSON, obj: fabric.Object) => {
        setLoading(false);
        if (onLoad) onLoad();
        setCurrentDimensions();
        obj.set({selectable: true});
        obj.setControlsVisibility({
          mtr: true,
          // @ts-expect-error Fabric wrapper has no TS types
          remove: true,
        });
      };
      canvasInstance.loadFromJSON(config, () => {}, reviver);
    };
    if (configJson) loadCanvas(configJson);

    setCurrentDimensions();

    setCanvas(canvasInstance);
    return () => {
      canvasInstance.dispose();
      window.removeEventListener('resize', setCurrentDimensions);
    };
  }, []);

  return {canvasRef, loading};
};
